import React, { useState, useEffect, useRef } from 'react';
import { Search, Filter, X, Eye, User } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import Pagination from '../components/Pagination';
import BookAppointmentModal from '../components/modals/BookAppointmentModal';
import { getMyDoctors, getPatientById } from '../redux/patientSlice';
import { getDoctorById } from '../redux/doctorSlice';
import { getCountries } from '../redux/masterSlice';
import { FilterPanel, FilterSection, FilterOptionList, FilterCountryList, FilterPopover } from '../components/common/FilterPanel';
import { useTranslation } from 'react-i18next';
import CommonLoader from '../components/CommonLoader';
import ErrorPage from '../components/ErrorPage';
import MasterLabel from '../components/common/MasterLabel';
import { SlidersHorizontal } from 'lucide-react';
import errorimg from '../assets/img/colorkit (1).svg';
import { getFileURL } from '../utils/fileURL';

const MySwal = withReactContent(Swal);

export default function MyDoctor() {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { t } = useTranslation(['doctorpage']);
    const userRole = localStorage.getItem('Role');
    const patientId = userRole === 'patient' ? localStorage.getItem('ID') : '';
    const language = localStorage.getItem('i18nextLng');
    const isInitialCall = useRef(true);
    const isPatientDataFetched = useRef(false);
    const { myDoctors, myDoctorsLoading, myDoctorsTotal, myDoctorsPage, myDoctorsLimit, myDoctorsTotalPages, error, selectedPatient, isLoading: patientLoading } = useSelector((state) => state.patient);
    const { selectedDoctor, loading: doctorLoading } = useSelector((state) => state.doctor);
    const { countries } = useSelector((state) => state.master);

    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(5);
    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
    const [searchError, setSearchError] = useState('');
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [tempFilters, setTempFilters] = useState({ gender: [], countryId: [] });
    const [filters, setFilters] = useState({ gender: [], countryId: [] });
    const [isBookAppointmentModalOpen, setIsBookAppointmentModalOpen] = useState(false);
    const [selectedDoctorForBooking, setSelectedDoctorForBooking] = useState(null);

    const handleBookClick = (doctor) => {
        setSelectedDoctorForBooking(doctor);
        dispatch(getDoctorById(doctor.doctorId || doctor.id));
        setIsBookAppointmentModalOpen(true);
    };

    const handleCloseBookModal = () => {
        setIsBookAppointmentModalOpen(false);
        setSelectedDoctorForBooking(null);
    };

    // Fetch countries on mount
    useEffect(() => {
        dispatch(getCountries({ status: 'active', limit: 100 }));
    }, [dispatch]);

    // Maximum length for search input
    const MAX_SEARCH_LENGTH = 50;

    const searchTimeoutRef = useRef(null);
    const inputRef = useRef(null);
    const filterButtonRef = useRef(null);
    const filterRef = useRef(null);

    // Debounce search query
    useEffect(() => {
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }

        // Only debounce if there's no error
        if (!searchError) {
            searchTimeoutRef.current = setTimeout(() => {
                // Trim the search query before setting it
                const trimmedSearch = searchQuery.trim();
                setDebouncedSearchQuery(trimmedSearch);
                setCurrentPage(1);
            }, 500);
        }

        return () => {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
        };
    }, [searchQuery, searchError]);

    // Sync tempFilters with filters when popup opens
    useEffect(() => {
        if (isFilterOpen) {
            setTempFilters({ ...filters });
        }
    }, [isFilterOpen]);

    // Close filter on click outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                filterRef.current &&
                !filterRef.current.contains(event.target) &&
                filterButtonRef.current &&
                !filterButtonRef.current.contains(event.target)
            ) {
                // Reset tempFilters to current filters when closing without applying
                setTempFilters({ ...filters });
                setIsFilterOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [filters]);

    // Refocus search input after loading
    useEffect(() => {
        const active = document.activeElement === inputRef.current;
        if (!active && inputRef.current && searchQuery.length > 0) {
            inputRef.current.focus();
        }
    }, [myDoctorsLoading]);

    // Fetch patient data to check profile status
    useEffect(() => {
        if (userRole === 'patient' && patientId && !isPatientDataFetched.current) {
            dispatch(getPatientById(patientId));
            isPatientDataFetched.current = true;
        }
    }, [dispatch, patientId, userRole]);

    // Fetch my doctors
    useEffect(() => {
        const buildMyDoctorsParams = (searchVal) => {
            const params = {
                patientId,
                page: currentPage,
                limit: itemsPerPage,
                search: searchVal,
            };
            if (filters.gender.length > 0) params.gender = filters.gender.join(',');
            if (filters.countryId.length > 0) params.countryId = filters.countryId.join(',');
            return params;
        };

        if (isInitialCall.current) {
            if (patientId) {
                dispatch(getMyDoctors(buildMyDoctorsParams(debouncedSearchQuery)));
            }
            isInitialCall.current = false;
            return;
        }

        // Subsequent calls on dependency changes
        // Only dispatch if there's no search error
        if (patientId && !searchError) {
            dispatch(getMyDoctors(buildMyDoctorsParams(debouncedSearchQuery.trim())));
        }
    }, [currentPage, itemsPerPage, debouncedSearchQuery, filters, patientId, dispatch, searchError]);

    const validateSearchInput = (value) => {
        // Remove any potentially dangerous characters
        const sanitized = value.replace(/[<>]/g, '');

        // Check length
        if (sanitized.length > MAX_SEARCH_LENGTH) {
            setSearchError(t('doctorpage.search_exceed_limit', `Search term cannot exceed ${MAX_SEARCH_LENGTH} characters`));
            return false;
        }

        // Check for only whitespace
        if (sanitized.trim().length === 0 && sanitized.length > 0) {
            setSearchError(t('doctorpage.search_only_spaces', 'Search term cannot contain only spaces'));
            return false;
        }

        setSearchError('');
        return true;
    };

    const handleSearch = (e) => {
        const value = e.target.value;

        // Sanitize input - remove dangerous characters
        const sanitized = value.replace(/[<>]/g, '');
        const finalValue = sanitized.length > 50 ? sanitized.substring(0, 50) : sanitized;

        // Always update searchQuery so the input stays in sync
        setSearchQuery(finalValue);

        // Validate input
        validateSearchInput(finalValue);
    };

    // Always map doctors to include fullName property
    const doctorsWithFullName = myDoctors.map((doctor) => ({
        ...doctor,
        fullName: `${doctor.name || ''} ${doctor.surname || ''}`.trim()
    }));

    const handlePageChange = (page, newItemsPerPage) => {
        if (newItemsPerPage && newItemsPerPage !== itemsPerPage) {
            setItemsPerPage(newItemsPerPage);
            setCurrentPage(1);
        } else {
            setCurrentPage(page);
        }
    };

    const currentDoctors = doctorsWithFullName;
    const totalItems = myDoctorsTotal;
    const totalPagesCount = myDoctorsTotalPages;
    const indexOfFirstDoctor = (currentPage - 1) * itemsPerPage;

    // Handle error state
    if (error) {
        if (error?.status >= 500 && error?.status <= 599) {
            return <ErrorPage error={error} pageName={t('doctorpage.my_doctors', 'My Doctors')} />;
        }
        if (error?.message === 'Network Error') {
            return <ErrorPage error={error} pageName={t('doctorpage.my_doctors', 'My Doctors')} />;
        }
        return <ErrorPage error={error} pageName={t('doctorpage.my_doctors', 'My Doctors')} />;
    }

    // Check if patient profile is incomplete - restrict viewing my doctors
    if (userRole === 'patient' && selectedPatient && selectedPatient.profileStatus === 'incomplete' && !patientLoading) {
        return (
            <div data-testid="my-doctor-profile-incomplete-container" className="h-full bg-white rounded-2xl p-4 sm:p-6 md:p-8 lg:p-10 flex items-center justify-center">
                <div className="text-center max-w-xs sm:max-w-sm md:max-w-md lg:max-w-lg xl:max-w-xl">
                    <img
                        data-testid="my-doctor-profile-incomplete-image"
                        src={errorimg}
                        alt={t('doctorpage.profile_incomplete_alt', 'Profile Incomplete')}
                        className="mx-auto w-32 h-32 sm:w-40 sm:h-40 md:w-48 md:h-48 lg:w-56 lg:h-56 xl:w-64 xl:h-64 object-contain"
                    />
                    <h2 data-testid="my-doctor-unable-to-view-title" className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-[#68c0c0] mb-3 sm:mb-4">
                        {t('doctorpage.unable_to_view', 'Unable to View My Doctors')}
                    </h2>
                    <p data-testid="my-doctor-profile-incomplete-message" className="text-sm sm:text-base md:text-lg text-gray-800 mb-2 sm:mb-3 md:mb-4">
                        {t('doctorpage.profile_incomplete_message', 'Your profile is incomplete. Please complete your profile to view your doctors.')}
                    </p>
                    <p data-testid="my-doctor-update-profile-message" className="text-xs sm:text-sm md:text-base text-gray-600 mb-4 sm:mb-6 md:mb-8">
                        {t('doctorpage.update_profile_message', 'Please update your profile information to continue.')}
                    </p>
                    <button
                        data-testid="my-doctor-go-to-profile-button"
                        onClick={() => navigate('/patientProfile')}
                        className="px-3 py-1.5 sm:px-4 sm:py-2 md:px-5 md:py-2.5 cursor-pointer bg-[#4ABDBD] text-white font-semibold rounded-lg shadow-sm text-xs sm:text-sm md:text-base transition-all hover:bg-[#3aa8a8] focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] focus:ring-opacity-50"
                    >
                        {t('doctorpage.go_to_profile', 'Go to Profile')}
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div data-testid="my-doctor-page-container" className="bg-white p-4 rounded-2xl shadow-md sm:p-6 w-full">
            {/* Header Row */}
            <div data-testid="my-doctor-page-header" className="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4">
                <h1 data-testid="my-doctor-page-title" className="text-2xl sm:text-3xl mr-4 font-bold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent ml-2">
                    {t('doctorpage.my_doctors', 'My Doctors')}
                </h1>
            </div>

            {/* Search + Filter Row */}
            <div data-testid="search-container" className="mb-6 flex flex-col sm:flex-row gap-2">
                <div className="relative w-full sm:w-80">
                    <input
                        ref={inputRef}
                        data-testid="search-input"
                        type="text"
                        placeholder={t("doctorpage.search_placeholder", "Search doctors...")}
                        value={searchQuery}
                        onChange={handleSearch}
                        disabled={false}
                        maxLength={MAX_SEARCH_LENGTH}
                        className={`w-full p-2 pl-10 pr-8 border rounded-xl focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] ${searchError
                            ? 'border-red-500 bg-red-50'
                            : 'border-gray-300'
                            } ${myDoctorsLoading ? "opacity-70" : ""}`}
                    />
                    <Search data-testid="search-icon" className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-[#4ABDBD]" />
                    {searchError && (
                        <p data-testid="search-error" className="text-red-500 text-xs mt-1 absolute -bottom-5 left-0">{searchError}</p>
                    )}
                </div>

                {/* Filter Button + Popup */}
                <div data-testid="payments-filter-container" className="relative w-full sm:w-auto">
                    <button
                        data-testid="filter-button"
                        ref={filterButtonRef}
                        onClick={() => setIsFilterOpen(!isFilterOpen)}
                        className="group flex items-center justify-center gap-2 h-full px-3 py-2.5 bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 font-semibold text-sm cursor-pointer"
                        aria-label={t('doctorpage.filter')}
                        aria-expanded={isFilterOpen}
                    >
                        <SlidersHorizontal className="w-5 h-5" />
                        {/* {t('doctorpage.filter')} */}
                    </button>

                    {isFilterOpen && (
                        <FilterPopover ref={filterRef}>
                            <FilterPanel
                                onClear={() => {
                                    setTempFilters({ gender: [], countryId: [] });
                                    setFilters({ gender: [], countryId: [] });
                                    setCurrentPage(1);
                                    setIsFilterOpen(false);
                                }}
                                onApply={() => {
                                    setFilters(tempFilters);
                                    setCurrentPage(1);
                                    setIsFilterOpen(false);
                                }}
                                clearLabel={t('doctorpage.clear')}
                                applyLabel={t('doctorpage.apply')}
                            >
                                <FilterSection title={t('doctorpage.gender')} isActive={tempFilters.gender.length > 0}>
                                    <FilterOptionList
                                        multiple
                                        value={tempFilters.gender}
                                        onChange={(val) => setTempFilters({ ...tempFilters, gender: val })}
                                        options={[
                                            { value: 'male', label: t('doctorpage.male') },
                                            { value: 'female', label: t('doctorpage.female') },
                                            { value: 'Not_Communicated', label: t('common.gender.notCommunicated', 'Not Communicated') },
                                            { value: 'other', label: t('doctorpage.other') },
                                        ]}
                                    />
                                </FilterSection>

                                <FilterSection title={t('doctorpage.country', 'Country')} isActive={tempFilters.countryId.length > 0}>
                                    <FilterCountryList
                                        multiple
                                        value={tempFilters.countryId}
                                        onChange={(val) => setTempFilters({ ...tempFilters, countryId: val })}
                                        countries={countries}
                                        searchPlaceholder={t('doctorpage.search_placeholder', 'Search...')}
                                    />
                                </FilterSection>
                            </FilterPanel>
                        </FilterPopover>
                    )}
                </div>
            </div>

            {/* ---- Applied Filters Display ---- */}
            {(filters.gender.length > 0 || filters.countryId.length > 0) && (
                <div data-testid="applied-filters-container" className="mb-6 bg-gradient-to-r from-blue-50/80 to-cyan-50/80 rounded-xl p-4 border border-gray-200 shadow-sm">
                    <div className="flex flex-wrap items-center gap-2">
                        <span data-testid="applied-filters-label" className="text-sm font-semibold text-gray-700 mr-2">
                            {t('doctorpage.applied_filters', 'Applied Filters')}:
                        </span>

                        {filters.gender.map((g) => (
                            <div key={`gender-${g}`} data-testid={`applied-filter-gender-${g}`} className="inline-flex items-center gap-2 bg-white px-3 py-1.5 rounded-lg border border-gray-300 shadow-sm">
                                <span className="text-xs text-gray-600">
                                    <span className="font-semibold">{t('doctorpage.gender')}:</span> {t(`doctorpage.${g}`)}
                                </span>
                                <button
                                    data-testid={`remove-gender-filter-${g}`}
                                    onClick={() => setFilters({ ...filters, gender: filters.gender.filter((x) => x !== g) })}
                                    className="text-gray-400 hover:text-red-500 transition-colors duration-200 cursor-pointer"
                                >
                                    <X className="h-3.5 w-3.5" />
                                </button>
                            </div>
                        ))}

                        {filters.countryId.map((cid) => (
                            <div key={`country-${cid}`} data-testid={`applied-filter-country-${cid}`} className="inline-flex items-center gap-2 bg-white px-3 py-1.5 rounded-lg border border-gray-300 shadow-sm">
                                <span className="text-xs text-gray-600">
                                    <span className="font-semibold">{t('doctorpage.country', 'Country')}:</span> <MasterLabel type="masterCountry" id={cid} field="countryName" fallback={countries?.find(c => c.countryId === cid)?.countryName || cid} />
                                </span>
                                <button
                                    data-testid={`remove-country-filter-${cid}`}
                                    onClick={() => setFilters({ ...filters, countryId: filters.countryId.filter((x) => x !== cid) })}
                                    className="text-gray-400 hover:text-red-500 transition-colors duration-200 cursor-pointer"
                                >
                                    <X className="h-3.5 w-3.5" />
                                </button>
                            </div>
                        ))}

                        <button
                            data-testid="clear-all-filters-button"
                            onClick={() => {
                                setFilters({ gender: [], countryId: [] });
                                setTempFilters({ gender: [], countryId: [] });
                                setCurrentPage(1);
                            }}
                            className="ml-auto inline-flex items-center gap-1.5 bg-gradient-to-r from-red-500 to-red-600 text-white px-3 py-1.5 rounded-lg text-xs font-semibold hover:from-red-600 hover:to-red-700 transition-all duration-200 shadow-sm hover:shadow-md cursor-pointer"
                        >
                            <X className="h-3.5 w-3.5" />
                            {t('doctorpage.clear_all', 'Clear All')}
                        </button>
                    </div>
                </div>
            )}

            {/* Table for larger screens, Cards for mobile */}
            {myDoctorsLoading ? (
                <div data-testid="my-doctor-loading-container" className="flex flex-col items-center justify-center py-4 pt-12">
                    <CommonLoader />
                    <span data-testid="my-doctor-loading-text" className="text-xl text-[#4ABDBD]">{t('doctorpage.loading', 'Loading...')}</span>
                </div>
            ) : (
                <>
                    {/* Table for sm and above */}
                    <div className="hidden sm:block overflow-x-auto rounded-md border border-gray-200">
                        <table data-testid="doctors-table" className="w-full border-collapse rounded-md overflow-hidden">
                            <thead>
                                <tr data-testid="table-header" className="bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white">
                                    <th data-testid="serial-no-header" className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('doctorpage.serial_no', 'Serial No')}</th>
                                    <th data-testid="name-header" className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('doctorpage.full_name', 'Full Name')}</th>
                                    <th data-testid="email-header" className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('doctorpage.email', 'Email')}</th>
                                    <th data-testid="phone-header" className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('doctorpage.phone', 'Phone')}</th>
                                    <th data-testid="specialty-header" className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('doctorpage.specialty', 'Specialty')}</th>
                                    <th data-testid="action-header" className="p-3 text-center text-xs font-bold uppercase tracking-wider">{t('doctorpage.action', 'Action')}</th>
                                </tr>
                            </thead>
                            <tbody data-testid="doctors-table-body">
                                {currentDoctors.length > 0 ? (
                                    currentDoctors.map((doctor, index) => {
                                        const serialNumber = indexOfFirstDoctor + index + 1;
                                        const isInactive = doctor.status === 'inactive' || doctor.availabilityStatus === false;
                                        const isUnavailable = doctor.availabilityStatus === false;
                                        return (
                                            <tr key={doctor.doctorId || doctor._id} data-testid={`doctor-row-${doctor.doctorId}`} className={`border-b border-gray-100/50 transition-all duration-300 group ${isInactive ? 'bg-gray-100/50' : 'hover:bg-gradient-to-r hover:from-blue-50/80 hover:to-cyan-50/80'}`}>
                                                <td data-testid={`doctor-serial-${doctor.doctorId}`} className={`p-3 text-xs transition-colors duration-300 ${isInactive ? 'text-gray-400' : 'text-gray-600 group-hover:text-gray-700'}`}>{serialNumber}</td>
                                                <td data-testid={`doctor-name-${doctor.doctorId}`} className={`p-3 text-xs transition-colors duration-300 ${isInactive ? 'text-gray-400' : 'text-gray-600 group-hover:text-gray-700'}`}>
                                                    <div className="flex items-center gap-3">
                                                        <div className="relative w-10 h-10 flex-shrink-0">
                                                            {doctor.image ? (
                                                                <img
                                                                    src={getFileURL(doctor.image)}
                                                                    alt={doctor.fullName || t('doctorpage.doctor_alt', 'Doctor')}
                                                                    className={`w-10 h-10 rounded-full object-cover shadow-md ${isInactive ? 'opacity-60' : ''}`}
                                                                    onError={(e) => {
                                                                        e.target.style.display = 'none';
                                                                        const fallback = e.target.parentElement.querySelector('.image-fallback');
                                                                        if (fallback) fallback.style.display = 'flex';
                                                                    }}
                                                                />
                                                            ) : null}
                                                            <div
                                                                className={`image-fallback ${doctor.image ? 'hidden' : 'flex'} absolute inset-0 w-10 h-10 rounded-full ${isInactive ? 'bg-gray-400' : 'bg-[#4ABDBD]'} items-center justify-center shadow-md`}
                                                            >
                                                                {doctor.fullName ? (
                                                                    <span className="text-white text-sm font-bold uppercase">
                                                                        {doctor.fullName.charAt(0).toUpperCase()}
                                                                    </span>
                                                                ) : (
                                                                    <User className="w-5 h-5 text-white" />
                                                                )}
                                                            </div>
                                                        </div>
                                                        <span>{doctor.fullName || '-'}</span>
                                                    </div>
                                                </td>
                                                <td data-testid={`doctor-email-${doctor.doctorId}`} className={`p-3 text-xs transition-colors duration-300 ${isInactive ? 'text-gray-400' : 'text-gray-600 group-hover:text-gray-700'}`}>{doctor.email || '-'}</td>
                                                <td data-testid={`doctor-phone-${doctor.doctorId}`} className={`p-3 text-xs transition-colors duration-300 ${isInactive ? 'text-gray-400' : 'text-gray-600 group-hover:text-gray-700'}`}>{doctor.phone || '-'}</td>
                                                <td
                                                    data-testid={`doctor-specialty-${doctor.doctorId}`}
                                                    className={`p-3 text-xs transition-colors duration-300 ${isInactive ? 'text-gray-400' : 'text-gray-600 group-hover:text-gray-700'}`}
                                                >
                                                    {doctor?.specialty?.length > 0 ? (
                                                        <div className="flex flex-wrap gap-1">
                                                            {doctor.specialty.map((spec, index) => {
                                                                const specialtyName = typeof spec === 'object' ? spec.name : spec;
                                                                // Legacy `doctor.specialty` often lacks specialtyId — fall back to
                                                                // matching against `appointmentSpeciality` where the master ID lives.
                                                                const specialtyId = (typeof spec === 'object' && spec.specialtyId)
                                                                    || doctor?.appointmentSpeciality?.find((s) => s.name === specialtyName)?.specialtyId
                                                                    || null;
                                                                const specialtyColor = typeof spec === 'object' ? spec.color : '#4ABDBD';
                                                                return (
                                                                    <span
                                                                        key={index}
                                                                        className={`px-2 py-1 rounded-md font-medium whitespace-nowrap ${isInactive ? 'opacity-60' : ''}`}
                                                                        style={{
                                                                            backgroundColor: `${specialtyColor}15`,
                                                                            color: specialtyColor,
                                                                            border: `1px solid ${specialtyColor}30`
                                                                        }}
                                                                    >
                                                                        <MasterLabel type="masterSpecialty" id={specialtyId} field="name" fallback={specialtyName} />
                                                                    </span>
                                                                );
                                                            })}
                                                        </div>
                                                    ) : (
                                                        '-'
                                                    )}
                                                </td>
                                                <td data-testid={`doctor-actions-${doctor.doctorId}`} className="p-3 text-xs">
                                                    {isInactive ? (
                                                        <div className="flex items-center justify-center">
                                                            {isUnavailable && doctor.status !== 'inactive' ? (
                                                                <span className="text-orange-400 text-xs font-medium">{t('doctorpage.unavailable', 'Unavailable')}</span>
                                                            ) : (
                                                                <span className="text-gray-400 text-xs font-medium">{t('doctorpage.inactive', 'Inactive')}</span>
                                                            )}
                                                        </div>
                                                    ) : (
                                                        <div className="flex items-center justify-center gap-2">
                                                            <button
                                                                data-testid={`view-doctor-${doctor.doctorId}`}
                                                                onClick={() => navigate('/doctorProfile', { state: { key: doctor.doctorId } })}
                                                                className="group/btn text-[#4ABDBD] hover:text-white p-2 rounded-xl bg-gradient-to-r from-blue-50 to-cyan-50 hover:from-[#4ABDBD] hover:to-[#3AA8A8] shadow hover:shadow-lg cursor-pointer transition-all duration-300 hover:scale-110 relative overflow-hidden"
                                                            >
                                                                <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent opacity-0 group-hover/btn:opacity-100 transition-opacity duration-300"></div>
                                                                <Eye data-testid={`view-icon-${doctor.doctorId}`} className="h-4 w-4 relative z-10" />
                                                            </button>
                                                            <button
                                                                data-testid={`book-doctor-${doctor.doctorId}`}
                                                                onClick={() => handleBookClick(doctor)}
                                                                className="text-white px-4 py-2 rounded-xl bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] hover:from-[#3AA8A8] hover:to-[#2D8A8A] shadow hover:shadow-lg cursor-pointer transition-all duration-300 hover:scale-105 text-xs font-semibold"
                                                            >
                                                                {t('doctorpage.book', 'Book')}
                                                            </button>
                                                        </div>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })
                                ) : (
                                    <tr data-testid="no-doctors-row-desktop">
                                        <td data-testid="no-doctors-message" colSpan="6" className="p-3 text-center text-gray-500">
                                            {t('doctorpage.no_doctors_found', 'No doctors found')}
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Card layout for mobile screens */}
                    <div className="block sm:hidden space-y-4">
                        {currentDoctors.length > 0 ? (
                            currentDoctors.map((doctor, index) => {
                                const serialNumber = indexOfFirstDoctor + index + 1;
                                const isInactive = doctor.status === 'inactive' || doctor.availabilityStatus === false;
                                const isUnavailable = doctor.availabilityStatus === false;
                                return (
                                    <div
                                        key={doctor.doctorId || doctor._id}
                                        data-testid={`doctor-card-${doctor.doctorId}`}
                                        className={`p-4 rounded-xl shadow-md transition-all duration-300 ${isInactive ? 'bg-gray-100/50' : 'bg-gradient-to-r bg-gray-50 hover:shadow-lg'}`}
                                    >
                                        <div className="flex justify-between items-center mb-2">
                                            <span data-testid={`doctor-serial-${doctor.doctorId}`} className={`text-xs font-bold ${isInactive ? 'text-gray-400' : 'text-[#4ABDBD]'}`}>
                                                {t('doctorpage.serial_no', 'Serial No')}: {serialNumber}
                                            </span>
                                            <div className="flex items-center gap-2">
                                                {isInactive ? (
                                                    isUnavailable && doctor.status !== 'inactive' ? (
                                                        <span className="text-orange-400 text-xs font-medium">{t('doctorpage.unavailable', 'Unavailable')}</span>
                                                    ) : (
                                                        <span className="text-gray-400 text-xs font-medium">{t('doctorpage.inactive', 'Inactive')}</span>
                                                    )
                                                ) : (
                                                    <>
                                                        <button
                                                            data-testid={`view-doctor-${doctor.doctorId}`}
                                                            onClick={() => navigate('/doctorProfile', { state: { key: doctor.doctorId } })}
                                                            className="group/btn text-[#4ABDBD] hover:text-white p-2 rounded-xl bg-gradient-to-r from-blue-50 to-cyan-50 hover:from-[#4ABDBD] hover:to-[#3AA8A8] shadow hover:shadow-lg cursor-pointer transition-all duration-300 hover:scale-110 relative overflow-hidden"
                                                        >
                                                            <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent opacity-0 group-hover/btn:opacity-100 transition-opacity duration-300"></div>
                                                            <Eye data-testid={`view-icon-${doctor.doctorId}`} className="h-4 w-4 relative z-10" />
                                                        </button>
                                                        <button
                                                            data-testid={`book-doctor-${doctor.doctorId}`}
                                                            onClick={() => handleBookClick(doctor)}
                                                            className="text-white px-4 py-2 rounded-xl bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] hover:from-[#3AA8A8] hover:to-[#2D8A8A] shadow hover:shadow-lg cursor-pointer transition-all duration-300 text-xs font-semibold"
                                                        >
                                                            {t('doctorpage.book', 'Book')}
                                                        </button>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                        <div className="space-y-2">
                                            <div data-testid={`doctor-name-${doctor.doctorId}`} className={`flex items-center gap-3 text-sm font-semibold ${isInactive ? 'text-gray-400' : 'text-gray-800'}`}>
                                                <div className="relative w-10 h-10 flex-shrink-0">
                                                    {doctor.image ? (
                                                        <img
                                                            src={getFileURL(doctor.image)}
                                                            alt={doctor.fullName || t('doctorpage.doctor_alt', 'Doctor')}
                                                            className={`w-10 h-10 rounded-full object-cover shadow-md ${isInactive ? 'opacity-60' : ''}`}
                                                            onError={(e) => {
                                                                e.target.style.display = 'none';
                                                                const fallback = e.target.parentElement.querySelector('.image-fallback');
                                                                if (fallback) fallback.style.display = 'flex';
                                                            }}
                                                        />
                                                    ) : null}
                                                    <div
                                                        className={`image-fallback ${doctor.image ? 'hidden' : 'flex'} absolute inset-0 w-10 h-10 rounded-full ${isInactive ? 'bg-gray-400' : 'bg-[#4ABDBD]'} items-center justify-center shadow-md`}
                                                    >
                                                        {doctor.fullName ? (
                                                            <span className="text-white text-sm font-bold uppercase">
                                                                {doctor.fullName.charAt(0).toUpperCase()}
                                                            </span>
                                                        ) : (
                                                            <User className="w-5 h-5 text-white" />
                                                        )}
                                                    </div>
                                                </div>
                                                <div>
                                                    <span className="font-bold">{t('doctorpage.full_name', 'Full Name')}:</span> {doctor.fullName || '-'}
                                                </div>
                                            </div>
                                            <div data-testid={`doctor-email-${doctor.doctorId}`} className={`text-xs ${isInactive ? 'text-gray-400' : 'text-gray-600'}`}>
                                                <span className="font-bold">{t('doctorpage.email', 'Email')}:</span> {doctor.email || '-'}
                                            </div>
                                            <div data-testid={`doctor-phone-${doctor.doctorId}`} className={`text-xs ${isInactive ? 'text-gray-400' : 'text-gray-600'}`}>
                                                <span className="font-bold">{t('doctorpage.phone', 'Phone')}:</span> {doctor.phone || '-'}
                                            </div>
                                            <div data-testid={`doctor-specialty-${doctor.doctorId}`} className={`text-xs ${isInactive ? 'text-gray-400' : 'text-gray-600'}`}>
                                                <span className="font-bold">{t('doctorpage.specialty', 'Specialty')}:</span>
                                                {doctor?.specialty?.length > 0 ? (
                                                    <div className="flex flex-wrap gap-1 mt-1">
                                                        {doctor.specialty.map((spec, index) => {
                                                            const specialtyName = typeof spec === 'object' ? spec.name : spec;
                                                            const specialtyId = (typeof spec === 'object' && spec.specialtyId)
                                                                || doctor?.appointmentSpeciality?.find((s) => s.name === specialtyName)?.specialtyId
                                                                || null;
                                                            const specialtyColor = typeof spec === 'object' ? spec.color : '#4ABDBD';
                                                            return (
                                                                <span
                                                                    key={index}
                                                                    className={`px-2 py-1 rounded-md font-medium whitespace-nowrap ${isInactive ? 'opacity-60' : ''}`}
                                                                    style={{
                                                                        backgroundColor: `${specialtyColor}15`,
                                                                        color: specialtyColor,
                                                                        border: `1px solid ${specialtyColor}30`
                                                                    }}
                                                                >
                                                                    <MasterLabel type="masterSpecialty" id={specialtyId} field="name" fallback={specialtyName} />
                                                                </span>
                                                            );
                                                        })}
                                                    </div>
                                                ) : (
                                                    ' -'
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })
                        ) : (
                            <div data-testid="no-doctors-row-mobile" className="p-3 text-center text-gray-500">
                                {t('doctorpage.no_doctors_found', 'No doctors found')}
                            </div>
                        )}
                    </div>
                </>
            )}

            {/* Pagination */}
            {!myDoctorsLoading && totalItems > 0 && (
                <Pagination
                    totalItems={totalItems}
                    currentPage={currentPage}
                    itemsPerPage={itemsPerPage}
                    onPageChange={handlePageChange}
                    custombutton={currentDoctors.length === 0 ? 'disable' : ''}
                />
            )}

            <BookAppointmentModal
                isOpen={isBookAppointmentModalOpen}
                onClose={handleCloseBookModal}
                doctor={selectedDoctor}
                loading={doctorLoading}
                onAppointmentBooked={handleCloseBookModal}
            />
        </div>
    );
}

